<script setup lang="tsx">
import { ref, onBeforeMount } from 'vue'
import http from '@/services/http'
import { useI18n } from 'vue-i18n'
import AssetFilters from './AssetFilters.vue'
import OrderLabel from '@/components/widgets/OrderLabel.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import useAssetColumns from './columns'
import columnStore from '@/stores/column'
import type { Asset, PageRequest, PageResponse, Response } from '@/models/api-generated'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import CustomHeader from '../custom_field/CustomHeader.vue'
import CustomRow from '../custom_field/CustomRow.vue'
import { fetchCustomValuesForPage, type CustomValueMap } from '@/services/custom'
import { assetRefreshCallback } from '@/components/business_processes/shared'
import { getResponsibleIcon, averageResidual, hasPermission } from '@/services/utils'
import sliderStore from '@/stores/slider'
import type { AssetFilter } from '@/models/filters-generated'
import PlusButton from '../widgets/PlusButton.vue'
import SubAssetTable from '../sub_asset/SubAssetTable.vue'
import { riskConfig, getRiskConfig } from '../risk_management/riskConfig'
import { h } from 'vue'
import RisksTooltip from '../risk_management/RisksTooltip.vue'
import RiskScoreBadge from '../risk_management/RiskScoreBadge.vue'
import { PermOwnRiskRead, PermAllRiskRead } from '@/models/enums-generated'
import { viewing } from '@/services/utils'

const props = defineProps({
	component: { default: 'assets' },
	disableRowClick: { type: Boolean },
	bpId: { type: Number },
	bpAdd: { type: Boolean },
})

const emits = defineEmits<{
	checked: [id: number]
	unchecked: [id: number]
}>()

const slider = sliderStore()

const model = ref<PageResponse<Asset>>()
const customValues = ref<CustomValueMap>()
const isLoading = ref(false)
const { columns, custom } = useAssetColumns(props.component)
const { t } = useI18n()
const root = columnStore().defineComponent(props.component, fetch)
assetRefreshCallback.value ??= []
assetRefreshCallback.value.push(root.triggerFetch)

const toggleCheckbox = (assetId: number, checked: boolean) => {
	if (!model.value) return
	const index = model.value.items.findIndex((a) => a.id === assetId)
	if (index !== -1) {
		model.value.items[index].isChecked = checked
	} else {
		const baseAsset = model.value.items.find((a) => a.id === assetId)
		if (baseAsset) model.value.items.push({ ...baseAsset, isChecked: checked })
	}

	if (checked) emits('checked', assetId)
	else emits('unchecked', assetId)
}

const checkAllAssets = (checked: boolean) => {
	const ids = model.value?.items.map((a) => a.id) ?? []

	ids.forEach((id) => {
		toggleCheckbox(id, checked)
	})
}

async function fetch(signal: AbortSignal) {
	const expanded: number[] = []
	for (const e of model.value?.items ?? []) {
		if (e.isExpanded) expanded.push(e.id)
	}

	const request: PageRequest<AssetFilter> = {
		page: root.pagination.page,
		limit: root.pagination.limit,
		orderBy: root.orderValue(),
		filter: root.filterValue() as AssetFilter,
	}

	if (props.bpId) request.filter.bpId = props.bpId
	if (props.bpAdd) request.filter.bpAdd = props.bpAdd

	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<Asset>>>(`api/asset/list`, request, { signal })
	customValues.value = await fetchCustomValuesForPage('assets', data)
	isLoading.value = false

	if (data.data) model.value = data.data

	for (const e of expanded) {
		const asset = model.value?.items.find((x) => x.id === e)
		if (asset) asset.isExpanded = true
	}
}

function rowClick(e: MouseEvent, asset: Asset) {
	const el = e.target as HTMLElement
	if (el.tagName === 'I') return

	slider.openAsset(asset.id, asset.name)
}

const getIconTitle = (isActive: boolean, isConnected: boolean) => {
	let title = ''

	if (isActive) {
		if (!isConnected) {
			title = t('asset.status.connectionMissing')
		} else {
			title = t('asset.status.active')
		}
	} else {
		title = t('asset.status.notActive')
	}

	return title
}

function goToRisks(names: string[]) {
	if (viewing.value === 0) return
	const params = new URLSearchParams()
	names.forEach((name) => {
		params.append('name', name)
	})
	window.location.href = `/app/risk-management?${params}`
}

onBeforeMount(async () => {
	if (viewing.value === 0) return
	if (hasPermission(PermOwnRiskRead, PermAllRiskRead)) riskConfig.value = await getRiskConfig()
})
</script>

<template>
	<AssetFilters :component="props.component"></AssetFilters>
	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<div class="table-responsive w-100">
			<table class="table">
				<thead>
					<tr>
						<th v-if="props.disableRowClick">
							<input class="form-check-input" type="checkbox" @change="checkAllAssets(($event.target as HTMLInputElement).checked)" />
						</th>

						<th v-if="!props.disableRowClick"></th>

						<th v-if="columns.numSubAssets.value.isVisible">
							{{ $t('asset.sub', 2) }}
						</th>
						<th v-if="columns.inventoryNr.value.isVisible">
							<OrderLabel v-model="columns.inventoryNr.value">{{ $t('asset.inventoryNr') }}</OrderLabel>
						</th>
						<th v-if="columns.name.value.isVisible">
							<OrderLabel v-model="columns.name.value">{{ $t('misc.name') }}</OrderLabel>
						</th>
						<th v-if="columns.instance.value.isVisible">
							<OrderLabel v-model="columns.instance.value">{{ $t('instance') }}</OrderLabel>
						</th>
						<th v-if="columns.location.value.isVisible">
							<OrderLabel v-model="columns.location.value">{{ $t('location') }}</OrderLabel>
						</th>
						<th v-if="columns.price.value.isVisible">
							<OrderLabel v-model="columns.price.value">{{ $t('price') }}</OrderLabel>
						</th>
						<th v-if="columns.responsible.value.isVisible">
							<OrderLabel v-model="columns.responsible.value">{{ $t('responsible') }}</OrderLabel>
						</th>
						<th v-if="columns.status.value.isVisible">
							<OrderLabel v-model="columns.status.value">{{ $t('asset.status.label') }}</OrderLabel>
						</th>
						<th v-if="columns.comment.value.isVisible">
							<OrderLabel v-model="columns.comment.value">{{ $t('misc.comment') }}</OrderLabel>
						</th>
						<th v-if="columns.residual.value.isVisible && viewing > 0">{{ columns.residual.value.label }}</th>
						<th v-if="columns.risks.value.isVisible && viewing > 0">{{ columns.risks.value.label }}</th>
						<CustomHeader :columns="custom"></CustomHeader>
					</tr>
				</thead>
				<tbody v-if="model">
					<template v-for="(asset, i) in model.items" :key="asset.id">
						<tr class="asset-row" :class="{ odd: i % 2 != 0 }" @mouseup.stop="(e) => !props.disableRowClick && rowClick(e, asset)">
							<td v-if="props.disableRowClick">
								<input
									:id="'asset-checkbox' + asset.id"
									class="form-check-input"
									type="checkbox"
									:checked="asset.isChecked"
									@change="toggleCheckbox(asset.id, ($event.target as HTMLInputElement).checked)"
								/>
							</td>

							<td v-if="!props.disableRowClick">
								<PlusButton v-model="model.items[i].isExpanded" />
							</td>

							<td v-if="columns.numSubAssets.value.isVisible">
								{{ asset.subAssets?.length ?? 0 }}
							</td>
							<td v-if="columns.inventoryNr.value.isVisible">
								{{ asset.inventoryNr }}
							</td>
							<td v-if="columns.name.value.isVisible">
								{{ asset.name }}
							</td>
							<td v-if="columns.instance.value.isVisible">
								{{ asset.instance.name }}
							</td>
							<td v-if="columns.location.value.isVisible">
								{{ asset.location }}
							</td>
							<td v-if="columns.price.value.isVisible">
								{{ asset.price }}
							</td>
							<td v-if="columns.responsible.value.isVisible">
								<i v-if="asset.responsible" :class="getResponsibleIcon(asset.responsible)"></i>
								{{ asset.responsible ? asset.responsible.label : '-' }}
							</td>
							<td v-if="columns.status.value.isVisible">
								<i
									v-one-tooltip="getIconTitle(!!asset.status, asset.isConnected)"
									:class="[
										'bi',
										asset.status && !asset.isConnected ? 'bi-exclamation-circle-fill' : 'bi-circle-fill',
										asset.status ? 'active' : 'inactive',
									]"
								></i>
							</td>
							<td v-if="columns.comment.value.isVisible">
								{{ asset.comment }}
							</td>
							<td v-if="columns.residual.value.isVisible && viewing > 0">
								<template v-if="asset.risks && asset.risks.length > 0">
									<RiskScoreBadge :model-value="averageResidual(asset.risks)" />
								</template>
							</td>
							<td v-if="columns.risks.value.isVisible && viewing > 0" class="text-center">
								<span
									class="risks-cell"
									v-one-tooltip="{
										content: () => h(RisksTooltip, { risks: asset.risks }),
									}"
									@click.stop="goToRisks(asset.risks.map((r) => r.name))"
								>
									{{ asset.risks ? asset.risks.length : 0 }}
								</span>
							</td>
							<CustomRow :columns="custom" v-model="customValues" :row-id="asset.id"></CustomRow>
						</tr>
						<tr v-if="asset.isExpanded" class="group-row">
							<td colspan="100%" class="ps-4">
								<div class="d-flex justify-content-center mt-3">
									<button
										:data-test="'add-new-sub-asset-' + asset.id"
										type="button"
										class="btn btn-outline-primary btn-sm"
										@click="slider.openSubAsset(asset, 0, `${$t('misc.add')} (${asset.name})`)"
									>
										<i class="bi bi-plus-circle me-1"></i> {{ $t('asset.addSub') }}
									</button>
								</div>
								<SubAssetTable
									:component="'sub_' + props.component"
									:asset="asset"
									v-model="model.items[i].subAssets"
									:disableRowClick="props.disableRowClick"
								/>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
		</div>
	</LoadingContainer>
	<Pagination v-model="root.pagination" :total-pages="model?.pages" :tableTitle="props.component" />
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

.asset-row > td:hover, :deep(.sub-asset-row table td:hover)
    cursor: pointer

:deep(.active)
    color: #62d837

:deep(.inactive)
    color: #ff5402
</style>
